import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../../constants/routes';

function SubjectProgressCard({ subject, expanded, onToggle }) {
  const progressLabel = `${subject.lessonsCompleted}/${subject.lessonsTotal} Lessons Completed`;
  const continueHref = subject.courseId ? ROUTES.COURSE_DETAIL(subject.courseId) : null;

  return (
    <article className="w-[598px] rounded-2xl bg-white shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
      <div className="px-6 py-5">
        <div className="flex items-center justify-between gap-4">
          <h3 className="text-base font-bold tracking-wide text-[#1B1B1B] sm:text-[24px]">
            {subject.name}
          </h3>
          <span className="shrink-0 text-[14px] font-medium text-[#666666]">{progressLabel}</span>
        </div>

        <div className="mt-4 flex h-2 overflow-hidden rounded-full bg-[#E9E9E9]">
          {subject.segments.map((segment, index) => (
            <div
              key={`${subject.id}-segment-${index}`}
              className="h-full"
              style={{ width: `${segment.width}%`, backgroundColor: segment.color }}
            />
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between">
          {continueHref ? (
            <Link
              to={continueHref}
              className="flex h-10 items-center gap-2 rounded-lg bg-[#009DA8] px-5 text-[12px] font-semibold text-white shadow-sm transition"
            >
              Continue
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-white">
                <svg className="h-3 w-3 text-[#1A87CC]" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M8 5v14l11-7z" />
                </svg>
              </span>
            </Link>
          ) : (
            <button
              type="button"
              disabled
              className="flex h-10 cursor-not-allowed items-center gap-2 rounded-lg bg-[#009DA8]/50 px-5 text-[12px] font-semibold text-white shadow-sm"
            >
              Continue
            </button>
          )}

          <button
            type="button"
            onClick={onToggle}
            className="flex items-center gap-1 text-sm font-semibold text-[#9369CC] transition"
            aria-expanded={expanded}
          >
            Details
            <svg
              className={`h-4 w-4 transition ${expanded ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-3 pb-4 pt-1">
          <div className="overflow-x-auto rounded-2xl">
            <table className="min-w-full text-left text-sm">
              <thead className="text-[#666666]">
                <tr className="border-b border-[#E7F1FF]">
                  <th className="px-4 py-3 text-[12px] font-semibold text-[#1B1B1B]">Chapter Name</th>
                  <th className="px-4 py-3 text-[12px] font-semibold text-[#1B1B1B]">No of Lessons</th>
                  <th className="px-4 py-3 text-[12px] font-semibold text-[#1B1B1B]">Time Watched</th>
                </tr>
              </thead>
              <tbody>
                {subject.chapters.length > 0 ? (
                  subject.chapters.map((chapter) => (
                    <tr key={chapter.id || chapter.name} className="border-b border-[#EEF4FF] last:border-b-0">
                      <td className="px-4 py-3 font-medium text-[#999999]">{chapter.name}</td>
                      <td className="px-4 py-3 font-medium text-[#999999]">{chapter.lessons}</td>
                      <td className="px-4 py-3 font-medium text-[#999999]">{chapter.timeWatched}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="px-4 py-6 text-center text-sm text-[#999999]">
                      No chapters started yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </article>
  );
}

export default function SubjectProgressList({ subjects = [], loading = false }) {
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    if (subjects.length > 0) {
      setOpenId((current) => current ?? subjects[0].id);
    } else {
      setOpenId(null);
    }
  }, [subjects]);

  if (loading) {
    return (
      <section className="space-y-5">
        {[1, 2, 3].map((item) => (
          <div key={item} className="h-[160px] w-[598px] animate-pulse rounded-2xl bg-white/80" />
        ))}
      </section>
    );
  }

  if (!subjects.length) {
    return (
      <section className="w-[598px] rounded-2xl bg-white px-6 py-10 text-center shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
        <p className="text-sm text-[#666666]">No subject progress available for this class yet.</p>
      </section>
    );
  }

  return (
    <section className="space-y-5">
      {subjects.map((subject) => (
        <SubjectProgressCard
          key={subject.id}
          subject={subject}
          expanded={openId === subject.id}
          onToggle={() => setOpenId((current) => (current === subject.id ? null : subject.id))}
        />
      ))}
    </section>
  );
}
